import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Clock, History, MessagesSquare, Users } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import microSimService from '../../services/microSimService';
import { useMicroSimPaths } from '../../utils/microSimPaths';
import { showError } from '../../utils/toast';

/**
 * The micro simulation library: every published topic, filterable by theme.
 * Trainers open a topic to read about it or preview it; running one with participants
 * starts from a live session's control screen.
 *
 * Below the topics, "Past micro sims" lists what this trainer has run, so any launch's
 * results can be reopened later (the team module only had a URL for that).
 */

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const MicroSimLibraryPage = () => {
  const navigate = useNavigate();
  const paths = useMicroSimPaths();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState('All');
  const [past, setPast] = useState([]);

  useEffect(() => {
    let alive = true;
    microSimService.list()
      .then((res) => { if (alive) setItems(res.microSims || []); })
      .catch((err) => showError(err.userMessage || 'Could not load micro simulations'))
      .finally(() => { if (alive) setLoading(false); });
    microSimService.history({ limit: 20 })
      .then((res) => { if (alive) setPast(res.launches || []); })
      .catch(() => { /* history is secondary; the topics still load */ });
    return () => { alive = false; };
  }, []);

  const themes = useMemo(() => ['All', ...new Set(items.map((i) => i.theme))], [items]);
  const shown = theme === 'All' ? items : items.filter((i) => i.theme === theme);

  return (
    <DashboardLayout title="Micro simulations">
      <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 lg:px-6">
        <button
          type="button"
          onClick={() => navigate('/trainer/simulations')}
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800 dark:text-neutral-400"
        >
          <ArrowLeft size={15} /> Simulations
        </button>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Micro simulations</h2>
            <p className="mt-1 max-w-2xl text-sm text-neutral-500 dark:text-neutral-400">
              Short paired practice conversations. Pick one to preview it; run it with your room from a live
              session’s control screen.
            </p>
          </div>
          {themes.length > 2 && (
            <div className="flex flex-wrap gap-1.5">
              {themes.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTheme(t)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    theme === t
                      ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className={`${STAT_CARD_BASE} h-40 animate-pulse`} />
        ) : shown.length === 0 ? (
          <div className={`${STAT_CARD_BASE} px-6 py-14 text-center`}>
            <MessagesSquare className="mx-auto text-neutral-300" size={28} />
            <p className="mt-3 text-sm font-semibold text-neutral-800 dark:text-neutral-100">No micro simulations yet</p>
            <p className="mt-1 text-xs text-neutral-500">Topics appear here once they are published.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((sim) => (
              <button
                key={sim.slug}
                type="button"
                onClick={() => navigate(paths.topic(sim.slug))}
                className={`${STAT_CARD_BASE} flex flex-col p-5 text-left transition-shadow hover:shadow-lg`}
              >
                <span className="self-start rounded-full bg-violet-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-inset ring-violet-100">
                  {sim.theme}
                </span>
                <h3 className="mt-3 text-base font-semibold text-neutral-900 dark:text-neutral-100">{sim.title}</h3>
                {sim.subtitle && <p className="mt-1 flex-1 text-sm text-neutral-500 dark:text-neutral-400">{sim.subtitle}</p>}
                <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
                  <span className="inline-flex items-center gap-1"><Users size={13} /> {sim.playerCount} people</span>
                  <span>{sim.roundCount} rounds</span>
                  <span>{sim.decisionCount} decisions</span>
                  {sim.estimatedMinutes && <span className="inline-flex items-center gap-1"><Clock size={13} /> ~{sim.estimatedMinutes} min</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {past.length > 0 && (
          <section>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <History size={15} /> Past micro sims
            </h3>
            <div className={`${STAT_CARD_BASE} mt-3 divide-y divide-neutral-100 dark:divide-neutral-800`}>
              {past.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => navigate(paths.launch(l.id))}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{l.title}</p>
                    <p className="truncate text-xs text-neutral-500">
                      {l.sessionName || 'Session'} · {formatDate(l.createdAt)} · {l.pairsCompleted} of {l.pairs} pairs finished
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-2">
                    {l.status === 'active' && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Running</span>}
                    <ChevronRight size={16} className="text-neutral-400" />
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MicroSimLibraryPage;
